/**
 * Overlay écran VS (dépassement) — modale thémée avec dés 3D et bonus combativité animé.
 */
import {
  createDiceCubeScene,
  DICE_ROLL_DURATION_MS,
  getDiceCubeElement,
  setDiceCubeFace,
} from './diceCube.js';
import { stripOwnDriverSuffix } from '@multirx/shared/game/driverDisplayLabel.js';
import { nationalityFlagMarkup } from '../components/nationalityFlag.js';
import { driverAvatarImageClass } from '@multirx/shared/game/driverAvatarCrop.js';
import {
  createOvertakeResultButton,
  resetOvertakeResultButton,
  revealOvertakeResultButton,
  type OvertakeResultButton,
} from '../ui/overtakeResultButton.js';

export interface OvertakeDisplayInfo {
  driverName: string;
  carName: string;
  nationality?: string;
  driverImagePath?: string;
  driverDivision?: number;
}

export interface OvertakeVSDetails {
  attackerRoll: number;
  defenderRoll: number;
  attackerCombativeness: number;
  defenderCombativeness: number;
  attackerTotal: number;
  defenderTotal: number;
}

/** Garde le résultat visible ~1 s avant d’enchaîner le duel / la suite. */
export const OVERTAKE_HOLD_AFTER_RESULT_MS = 1000;

const BONUS_HOLD_MS = 380;
const BONUS_DROP_MS = 550;
/** Filet UI : si l’anim dé ne finit jamais (onglet throttle, cube cassé), forcer la clôture. */
export const OVERTAKE_FLOW_HARD_TIMEOUT_MS = 8000;

interface OvertakeSideDiceUI {
  scene: HTMLDivElement;
  bonusEl: HTMLSpanElement;
  combatValueEl: HTMLElement;
  totalValueEl: HTMLElement;
  totalRow: HTMLDivElement;
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function toAvatarMarkup(display: OvertakeDisplayInfo): string {
  const src = display.driverImagePath?.trim();
  if (src) {
    const safeSrc = escapeHtml(src);
    const safeAlt = escapeHtml(display.driverName);
    const avatarClass = driverAvatarImageClass(
      'overtake-side-avatar-image',
      display.driverDivision,
      src
    );
    return `<img src="${safeSrc}" alt="${safeAlt}" class="${avatarClass}" />`;
  }
  const base = stripOwnDriverSuffix(display.driverName);
  return escapeHtml(base.slice(0, 2).toUpperCase() || '??');
}

export class OvertakeOverlay {
  private wrapper: HTMLDivElement | null = null;
  private vsPanel: HTMLDivElement | null = null;
  private leftDriver: HTMLDivElement | null = null;
  private leftAvatar: HTMLDivElement | null = null;
  private leftCar: HTMLDivElement | null = null;
  private leftStats: HTMLDivElement | null = null;
  private leftDice: OvertakeSideDiceUI | null = null;
  private rightDriver: HTMLDivElement | null = null;
  private rightAvatar: HTMLDivElement | null = null;
  private rightCar: HTMLDivElement | null = null;
  private rightStats: HTMLDivElement | null = null;
  private rightDice: OvertakeSideDiceUI | null = null;
  private resultRow: HTMLDivElement | null = null;
  private resultText: HTMLDivElement | null = null;
  private resultDetail: HTMLDivElement | null = null;
  private actionsRow: HTMLDivElement | null = null;
  private resultButton: OvertakeResultButton | null = null;

  private sequenceId = 0;
  private autoDoneTimer: ReturnType<typeof setTimeout> | null = null;
  private hardTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingComplete: (() => void) | null = null;

  constructor(container: HTMLElement) {
    this.createUI(container);
  }

  private buildSideDiceUI(): { root: HTMLDivElement; ui: OvertakeSideDiceUI } {
    const root = document.createElement('div');
    root.className = 'overtake-side-stats';

    const stack = document.createElement('div');
    stack.className = 'overtake-dice-stack';

    const wrap = document.createElement('div');
    wrap.className = 'overtake-dice-wrap';

    const scene = createDiceCubeScene('overtake-dice-scene');
    const bonusEl = document.createElement('span');
    bonusEl.className = 'overtake-combat-bonus';
    bonusEl.hidden = true;
    wrap.append(scene, bonusEl);

    const combatRow = document.createElement('div');
    combatRow.className = 'overtake-combat-row';
    combatRow.innerHTML = 'Combativité <strong class="overtake-combat-value">0</strong>';
    const combatValueEl = combatRow.querySelector('.overtake-combat-value') as HTMLElement;

    const totalRow = document.createElement('div');
    totalRow.className = 'overtake-total-row is-pending';
    totalRow.innerHTML =
      '<span class="overtake-total-label">Total</span><span class="overtake-total-value">—</span>';
    const totalValueEl = totalRow.querySelector('.overtake-total-value') as HTMLElement;

    stack.append(wrap, combatRow, totalRow);
    root.append(stack);

    return {
      root,
      ui: { scene, bonusEl, combatValueEl, totalValueEl, totalRow },
    };
  }

  private createUI(container: HTMLElement): void {
    const wrapper = document.createElement('div');
    wrapper.id = 'overtake-overlay';
    wrapper.className = 'overtake-overlay overtake-overlay--hud';
    wrapper.setAttribute('role', 'dialog');
    wrapper.setAttribute('aria-modal', 'false');
    wrapper.setAttribute('aria-label', 'Dépassement');

    const inner = document.createElement('div');
    inner.className = 'overtake-overlay-inner';

    const vsPanel = document.createElement('div');
    vsPanel.className = 'overtake-vs-panel';

    const left = document.createElement('div');
    left.className = 'overtake-side';
    this.leftAvatar = document.createElement('div');
    this.leftAvatar.className = 'overtake-side-avatar';
    this.leftAvatar.setAttribute('aria-hidden', 'true');
    this.leftDriver = document.createElement('div');
    this.leftDriver.className = 'overtake-side-driver';
    this.leftCar = document.createElement('div');
    this.leftCar.className = 'overtake-side-car';
    const leftBuilt = this.buildSideDiceUI();
    this.leftStats = leftBuilt.root;
    this.leftDice = leftBuilt.ui;
    left.append(this.leftAvatar, this.leftDriver, this.leftCar, this.leftStats);

    const vs = document.createElement('div');
    vs.className = 'overtake-vs-label';
    vs.innerHTML = `
      <span class="overtake-vs-char overtake-vs-char--left">V</span>
      <span class="overtake-vs-separator" aria-hidden="true"></span>
      <span class="overtake-vs-char overtake-vs-char--right">S</span>`;

    const right = document.createElement('div');
    right.className = 'overtake-side';
    this.rightAvatar = document.createElement('div');
    this.rightAvatar.className = 'overtake-side-avatar';
    this.rightAvatar.setAttribute('aria-hidden', 'true');
    this.rightDriver = document.createElement('div');
    this.rightDriver.className = 'overtake-side-driver';
    this.rightCar = document.createElement('div');
    this.rightCar.className = 'overtake-side-car';
    const rightBuilt = this.buildSideDiceUI();
    this.rightStats = rightBuilt.root;
    this.rightDice = rightBuilt.ui;
    right.append(this.rightAvatar, this.rightDriver, this.rightCar, this.rightStats);

    vsPanel.append(left, vs, right);

    const resultRow = document.createElement('div');
    resultRow.className = 'overtake-result-row';
    this.resultText = document.createElement('div');
    this.resultText.className = 'overtake-result-text';
    this.resultDetail = document.createElement('div');
    this.resultDetail.className = 'overtake-result-detail';
    resultRow.append(this.resultText, this.resultDetail);

    const actionsRow = document.createElement('div');
    actionsRow.className = 'overtake-overlay-actions';
    this.resultButton = createOvertakeResultButton(() => this.finishSequence());
    actionsRow.append(this.resultButton.wrap);

    inner.append(vsPanel, resultRow, actionsRow);
    wrapper.appendChild(inner);
    container.appendChild(wrapper);

    this.wrapper = wrapper;
    this.vsPanel = vsPanel;
    this.resultRow = resultRow;
    this.actionsRow = actionsRow;
  }

  private clearTimers(): void {
    if (this.autoDoneTimer !== null) {
      clearTimeout(this.autoDoneTimer);
      this.autoDoneTimer = null;
    }
    if (this.hardTimeoutTimer !== null) {
      clearTimeout(this.hardTimeoutTimer);
      this.hardTimeoutTimer = null;
    }
  }

  /**
   * Annule timers / séquence en cours.
   * `settle: true` invoque le callback GameScene (sinon isMovingToken /
   * overtakeInProgress restent coincés → softlock Auto / lancer).
   */
  private abortSequence(opts?: { settle?: boolean }): void {
    const cb = opts?.settle ? this.pendingComplete : null;
    this.clearTimers();
    this.sequenceId++;
    this.pendingComplete = null;
    this.wrapper?.classList.remove('is-entered');
    if (cb) cb();
  }

  private resetSideDice(ui: OvertakeSideDiceUI): void {
    const cube = getDiceCubeElement(ui.scene);
    cube?.classList.remove('overtake-dice-locked', 'rolling');
    cube?.classList.remove('show-1', 'show-2', 'show-3', 'show-4', 'show-5', 'show-6');
    cube?.classList.add('show-1');
    ui.bonusEl.classList.remove('is-visible', 'is-dropping');
    ui.bonusEl.hidden = true;
    ui.bonusEl.textContent = '';
    ui.totalRow.classList.add('is-pending');
    ui.totalValueEl.textContent = '—';
  }

  private showVSPanel(
    attacker: OvertakeDisplayInfo,
    defender: OvertakeDisplayInfo,
    details: OvertakeVSDetails
  ): void {
    if (
      !this.leftDriver ||
      !this.leftAvatar ||
      !this.leftCar ||
      !this.leftDice ||
      !this.rightDriver ||
      !this.rightAvatar ||
      !this.rightCar ||
      !this.rightDice ||
      !this.resultRow ||
      !this.actionsRow
    )
      return;

    const attackerFlag = attacker.nationality
      ? `${nationalityFlagMarkup(attacker.nationality, { className: 'overtake-driver-flag' })} `
      : '';
    const defenderFlag = defender.nationality
      ? `${nationalityFlagMarkup(defender.nationality, { className: 'overtake-driver-flag' })} `
      : '';
    this.leftDriver.innerHTML = `${attackerFlag}${escapeHtml(attacker.driverName)}`;
    this.leftCar.textContent = attacker.carName;
    this.rightDriver.innerHTML = `${defenderFlag}${escapeHtml(defender.driverName)}`;
    this.rightCar.textContent = defender.carName;
    this.leftAvatar.innerHTML = toAvatarMarkup(attacker);
    this.rightAvatar.innerHTML = toAvatarMarkup(defender);

    this.resetSideDice(this.leftDice);
    this.resetSideDice(this.rightDice);
    this.leftDice.combatValueEl.textContent = String(details.attackerCombativeness);
    this.rightDice.combatValueEl.textContent = String(details.defenderCombativeness);

    if (this.resultButton) resetOvertakeResultButton(this.resultButton);
    this.resultRow.classList.remove('is-visible');
    this.actionsRow.classList.add('is-visible');
  }

  private animateSideDice(
    ui: OvertakeSideDiceUI,
    roll: number,
    combat: number,
    total: number
  ): Promise<void> {
    return new Promise((resolve) => {
      setDiceCubeFace(ui.scene, roll, true);

      window.setTimeout(() => {
        const cube = getDiceCubeElement(ui.scene);
        cube?.classList.add('overtake-dice-locked');

        const revealTotal = (): void => {
          ui.totalValueEl.textContent = String(total);
          ui.totalRow.classList.remove('is-pending');
          window.setTimeout(resolve, 280);
        };

        if (combat <= 0) {
          revealTotal();
          return;
        }

        ui.bonusEl.textContent = `+${combat}`;
        ui.bonusEl.hidden = false;
        requestAnimationFrame(() => {
          ui.bonusEl.classList.add('is-visible');
        });

        window.setTimeout(() => {
          ui.bonusEl.classList.add('is-dropping');
          window.setTimeout(() => {
            ui.bonusEl.classList.remove('is-visible', 'is-dropping');
            ui.bonusEl.hidden = true;
            revealTotal();
          }, BONUS_DROP_MS);
        }, BONUS_HOLD_MS);
      }, DICE_ROLL_DURATION_MS);
    });
  }

  private showResultPanel(success: boolean, _details: OvertakeVSDetails): void {
    if (this.resultButton) revealOvertakeResultButton(this.resultButton, success);
    this.resultRow?.classList.remove('is-visible');
    this.actionsRow?.classList.add('is-visible');
  }

  startOvertakeFlow(
    attacker: OvertakeDisplayInfo,
    defender: OvertakeDisplayInfo,
    details: OvertakeVSDetails,
    success: boolean,
    onComplete: () => void,
    onResultReveal?: () => void
  ): void {
    if (!this.wrapper || !this.leftDice || !this.rightDice) {
      onResultReveal?.();
      onComplete();
      return;
    }

    // settle: si un flux précédent était encore ouvert, débloquer GameScene
    // (sinon isMovingToken/overtakeInProgress restent true indéfiniment).
    this.abortSequence({ settle: true });
    const mySeq = this.sequenceId;
    this.pendingComplete = onComplete;

    this.showVSPanel(attacker, defender, details);
    this.wrapper.classList.add('is-visible');
    this.wrapper.classList.remove('is-entered');
    requestAnimationFrame(() => {
      if (mySeq !== this.sequenceId) return;
      this.wrapper?.classList.add('is-entered');
    });

    this.hardTimeoutTimer = window.setTimeout(() => {
      if (mySeq !== this.sequenceId) return;
      console.warn('[MultiRX] Overlay dépassement bloqué — clôture forcée');
      this.showResultPanel(success, details);
      onResultReveal?.();
      this.finishSequence();
    }, OVERTAKE_FLOW_HARD_TIMEOUT_MS);

    void Promise.all([
      this.animateSideDice(
        this.leftDice,
        details.attackerRoll,
        details.attackerCombativeness,
        details.attackerTotal
      ),
      this.animateSideDice(
        this.rightDice,
        details.defenderRoll,
        details.defenderCombativeness,
        details.defenderTotal
      ),
    ]).then(() => {
      if (mySeq !== this.sequenceId) return;
      if (this.hardTimeoutTimer !== null) {
        clearTimeout(this.hardTimeoutTimer);
        this.hardTimeoutTimer = null;
      }
      this.showResultPanel(success, details);
      onResultReveal?.();
      this.autoDoneTimer = window.setTimeout(() => {
        if (mySeq !== this.sequenceId) return;
        this.finishSequence();
      }, OVERTAKE_HOLD_AFTER_RESULT_MS);
    });
  }

  private finishSequence(): void {
    const cb = this.pendingComplete;
    this.abortSequence();
    this.wrapper?.classList.remove('is-visible');
    // Remettre le bouton en pending : sinon il reste cliquable hors overlay
    // (faux positifs QA / double-clics fantômes).
    if (this.resultButton) resetOvertakeResultButton(this.resultButton);
    if (cb) cb();
  }

  hide(): void {
    this.abortSequence({ settle: true });
    this.wrapper?.classList.remove('is-visible');
    if (this.resultButton) resetOvertakeResultButton(this.resultButton);
  }

  destroy(): void {
    this.abortSequence({ settle: true });
    this.wrapper?.remove();
    this.wrapper = null;
  }
}
